/**
 * SelectionOverlay - Canvas-first element selection UI
 * 
 * Design Philosophy:
 * - Selection = Focus, not containment
 * - Soft glow instead of hard borders
 * - Minimal edge handles that appear on hover
 * - Clean, non-intrusive visual indicators
 * 
 * Uses React Portal to render outside the preview container
 * Handles scroll/resize tracking to keep overlay positioned correctly
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ContextToolbar from './ContextToolbar';

const SelectionOverlay = ({
  selectedElementId,
  selectedBlockId,
  blockProps,
  tokens,
  containerRef,
  onUpdateValue,
  onUpdateStyles,
  onUpdateMotion,
  onReplayAnimation,
  onDeselectElement
}) => {
  const [elementRect, setElementRect] = useState(null);
  const [elementType, setElementType] = useState('text');
  const [elementLabel, setElementLabel] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const rafRef = useRef(null);
  
  // Find and track the selected element's position
  const updateElementRect = useCallback(() => {
    if (!selectedElementId || !containerRef?.current) {
      setElementRect(null);
      return;
    }
    
    // Find the element by data attribute
    const element = containerRef.current.querySelector(
      `[data-tc-element-id="${selectedElementId}"]`
    );
    
    if (element) {
      const rect = element.getBoundingClientRect();
      
      // Adjust rect relative to viewport (for portal positioning)
      setElementRect({
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
        x: rect.x,
        y: rect.y
      });
      
      // Get element type and label from data attributes
      setElementType(element.dataset.tcElementType || 'text');
      setElementLabel(element.dataset.tcElementLabel || selectedElementId);
    } else {
      setElementRect(null);
    }
  }, [selectedElementId, containerRef]);

  // Update on selection change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateElementRect();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [selectedElementId, updateElementRect]);

  // Update on scroll
  useEffect(() => {
    if (!containerRef?.current) return;
    
    const container = containerRef.current;
    
    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateElementRect);
    };
    
    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [containerRef, updateElementRect]);

  // Observe DOM mutations
  useEffect(() => {
    if (!containerRef?.current || !selectedElementId) return;
    
    const observer = new MutationObserver(() => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateElementRect);
    });
    
    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
    
    return () => observer.disconnect();
  }, [containerRef, selectedElementId, updateElementRect]);

  // Don't render if no selection
  if (!selectedElementId || !elementRect) {
    return null;
  }

  // Check if element is visible in viewport
  const isVisible = elementRect.bottom > 0 && 
                    elementRect.top < window.innerHeight &&
                    elementRect.right > 0 &&
                    elementRect.left < window.innerWidth;

  if (!isVisible) {
    return null;
  }

  // Padding around the selection for glow effect
  const glowPadding = 4;

  return createPortal(
    <div 
      className="fixed inset-0 pointer-events-none z-[9998]"
      data-testid="selection-overlay"
    >
      {/* Soft glow background - feels like focus, not containment */}
      <div
        className="absolute transition-all duration-200 ease-out"
        style={{
          top: elementRect.top - glowPadding,
          left: elementRect.left - glowPadding,
          width: elementRect.width + glowPadding * 2,
          height: elementRect.height + glowPadding * 2,
          background: 'radial-gradient(ellipse at center, rgba(249, 115, 22, 0.12) 0%, transparent 70%)',
          borderRadius: '8px',
        }}
      />
      
      {/* Selection outline - subtle, rounded, with soft shadow */}
      <div
        className="absolute rounded-lg transition-all duration-200 ease-out"
        style={{
          top: elementRect.top - 1,
          left: elementRect.left - 1,
          width: elementRect.width + 2,
          height: elementRect.height + 2,
          border: '1.5px solid rgba(249, 115, 22, 0.6)',
          boxShadow: `
            0 0 0 1px rgba(249, 115, 22, 0.1),
            0 0 20px rgba(249, 115, 22, 0.15),
            inset 0 0 20px rgba(249, 115, 22, 0.05)
          `,
        }}
        data-testid="selection-outline"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Edge handles - appear on hover, positioned at midpoints */}
        <div 
          className={`absolute transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          style={{ pointerEvents: 'none' }}
        >
          {/* Top handle */}
          <div 
            className="absolute w-6 h-1.5 bg-orange-500 rounded-full"
            style={{ top: -3, left: '50%', transform: 'translateX(-50%)' }}
          />
          {/* Bottom handle */}
          <div 
            className="absolute w-6 h-1.5 bg-orange-500 rounded-full"
            style={{ bottom: -3, left: '50%', transform: 'translateX(-50%)' }}
          />
          {/* Left handle */}
          <div 
            className="absolute w-1.5 h-6 bg-orange-500 rounded-full"
            style={{ left: -3, top: '50%', transform: 'translateY(-50%)' }}
          />
          {/* Right handle */}
          <div 
            className="absolute w-1.5 h-6 bg-orange-500 rounded-full"
            style={{ right: -3, top: '50%', transform: 'translateY(-50%)' }}
          />
        </div>
        
        {/* Corner dots - small, subtle, always visible */}
        <div className="absolute -top-1 -left-1 w-2 h-2 bg-orange-500 rounded-full shadow-sm" />
        <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full shadow-sm" />
        <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-orange-500 rounded-full shadow-sm" />
        <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-orange-500 rounded-full shadow-sm" />
      </div>
      
      {/* Floating Toolbar */}
      <ContextToolbar
        elementRect={elementRect}
        elementType={elementType}
        elementId={selectedElementId}
        elementLabel={elementLabel}
        blockProps={blockProps}
        tokens={tokens}
        onUpdateValue={onUpdateValue}
        onUpdateStyles={onUpdateStyles}
        onUpdateMotion={onUpdateMotion}
        onReplayAnimation={onReplayAnimation}
        onClose={onDeselectElement}
        containerRef={containerRef}
      />
    </div>,
    document.body
  );
};

export default SelectionOverlay;
